import cx from 'classnames'
import React from 'react'

import { formatRating } from '@js/common/utils'

import Icon from '@f/elements/Icon'
import { PinIcon } from '@f/elements/Icon/PinIcon'

import { Nullable } from '@f-fertighaus/applications/Next/types/utility'
import { SearchResultsVendorDetails } from '@f-fertighaus/applications/SearchResults/partials/items/Types'
import { Rating as SearchResultRating } from '@f-fertighaus/components/ReviewRating/SearchResultRating'

interface CardTemplateProps {
  vendor: SearchResultsVendorDetails
  onClick: (event: React.MouseEvent<HTMLElement>) => void
  children: React.ReactNode
  className?: string
}

const isProOrPremium = (vendor: SearchResultsVendorDetails) =>
  vendor.branding_plan === 'pro' || vendor.branding_plan === 'premium'

const isBasicOrPro = (vendor: SearchResultsVendorDetails) =>
  vendor.branding_plan === 'basic' || vendor.branding_plan === 'pro'

const isPremiumWithBrandingColor = (vendor: SearchResultsVendorDetails) =>
  vendor.branding_plan === 'premium' &&
  vendor.branding_colors.branding_main_color

export const CardTemplate = ({
  vendor,
  onClick,
  children,
  className
}: CardTemplateProps) => {
  return (
    <div
      className={cx(
        'f-pos-rel f-m-a-020 SearchResultsTile-item',
        vendor.isTopPlacement && 'f-m-t-030',
        (vendor.isTopPlacement || vendor.isTopLocalPlacement) &&
          'is-top-placement'
      )}
      data-sel={`${
        vendor.isTopPlacement
          ? 'crd-vendor-tp'
          : vendor.top_local_placement
          ? 'crd-vendor-regio'
          : 'crd-vendor-dflt'
      }-${vendor.nid}`}>
      <div className='[ f-pos-rel ]'>
        <a
          href={vendor.url}
          className={cx(
            'f-d-bl f-overflow f-w-100 f-h-100 SearchResultsTile-image-container',
            isBasicOrPro(vendor) &&
              'SearchResultsTile-image-container--has-full-radius',
            isProOrPremium(vendor) &&
              'SearchResultsTile-image-container--has-border',
            className
          )}
          style={
            isPremiumWithBrandingColor(vendor)
              ? { borderColor: vendor.branding_colors.branding_main_color }
              : {}
          }
          onClick={onClick}>
          <>
            <img
              src={vendor.search_result_image['fill-400x300']}
              alt={vendor.name}
              height='225'
              width='300'
              className='SearchResultsTile-image'
              loading='lazy'
            />
            <img
              src={vendor.logo['search']}
              alt={`${vendor.name}-logo`}
              className='SearchResultsTile-logo'
            />
            {isProOrPremium(vendor) &&
              vendor.vendor_awards &&
              vendor.vendor_awards.length > 0 && (
                <img
                  src={vendor.vendor_awards[0]['max-100x100']}
                  alt={`${vendor.name}-badge`}
                  className='SearchResultsTile-badge'
                />
              )}
          </>
        </a>
      </div>
      {children}
    </div>
  )
}

interface HouseThumbnail {
  'fill-400x300': string
}

interface HouseThumbnailsProps {
  houseThumbnails: HouseThumbnail[]
  vendorName: string
  onClick: (event: React.MouseEvent<HTMLElement>) => void
}
export const HouseThumbnails = ({
  houseThumbnails,
  vendorName,
  onClick
}: HouseThumbnailsProps) => {
  return (
    <div
      className='f-d-flr f-fl-al-ce f-fl-jc-st f-fl-gap-005 f-m-t-005 f-m-b-010 SearchResultsTile-thumbnails-container'
      onClick={onClick}>
      {houseThumbnails.map((thumbnail, index) => (
        <div
          key={`${vendorName}-${thumbnail['fill-400x300']}`}
          className='SearchResultsTile-house-thumbnail-wrapper'>
          <img
            className='SearchResultsTile-house-thumbnail'
            src={thumbnail['fill-400x300']}
            alt={`${vendorName}-thumbnail-${index}`}
          />
        </div>
      ))}
    </div>
  )
}

interface VendorInfoBasicProps {
  name: string
  slogan: string
  onClick: (event: React.MouseEvent<HTMLElement>) => void
  children: React.ReactNode
  postcode: Nullable<string>
  top_local_placement: Nullable<boolean>
}

interface VendorInfoProProps extends VendorInfoBasicProps {
  number_of_houses: number
  number_of_employees: string | null
  year_of_foundation: number | null
}

interface VendorInfoPremiumProps extends VendorInfoProProps {
  branding_main_color: string
}

export const VendorInfoBasic = ({
  name,
  slogan,
  onClick,
  children,
  postcode,
  top_local_placement
}: VendorInfoBasicProps) => (
  <div
    className='f-d-flc f-fl-al-st f-fl-jc-st f-m-t-010 f-m-b-005 f-fl-gap-005'
    onClick={onClick}>
    <div className='f-t-tminor f-t-ellip f-w-100'>{name}</div>
    {top_local_placement && postcode && <TopLocalVendorBadge />}
    <div className='f-t-mini f-c-fo-neu200 f-t-ellip f-w-100'>{slogan}</div>
    {children}
  </div>
)

export const VendorInfoPro = ({
  name,
  slogan,
  year_of_foundation,
  number_of_employees,
  number_of_houses,
  onClick,
  postcode,
  top_local_placement,
  children
}: VendorInfoProProps) => {
  const hasVendorDetails =
    number_of_houses || number_of_employees || year_of_foundation
  return (
    <div
      className='f-d-flr f-fl-al-st f-fl-jc-sb f-m-t-010 f-m-b-005 SearchResultsVendor-vendor-info'
      onClick={onClick}>
      <div
        className={cx(
          'f-d-flc f-fl-al-st f-fl-jc-st f-fl-gap-005 SearchResultsVendor-vendor-name-slogan',
          hasVendorDetails
            ? 'SearchResultsVendor-vendor-name-slogan--is-column'
            : 'f-w-100'
        )}>
        <div className='f-t-tminor f-w-100 f-t-ellip'>{name}</div>
        {top_local_placement && postcode && <TopLocalVendorBadge />}
        <div className='f-t-mini f-c-fo-neu200 f-w-100 f-t-ellip'>{slogan}</div>
        {children}
      </div>
      {hasVendorDetails && (
        <div className='f-d-flc f-fl-al-en f-fl-jc-ce f-fl-gap-005 f-no-shrink'>
          {Boolean(number_of_houses) && (
            <div className='f-d-flr f-fl-al-ce f-fl-jc-en f-m-b-005'>
              <div className='f-t-micro f-m-r-010'>
                {`${number_of_houses} ${
                  number_of_houses === 1 ? 'Haus' : 'Häuser'
                }`}
              </div>
              <Icon
                icon='house-16px'
                className='f-icon-020 f-c-st-pri f-no-shrink'
              />
            </div>
          )}
          {Boolean(number_of_employees) && (
            <div className='f-d-flr f-fl-al-ce f-fl-jc-en f-m-b-005'>
              <div className='f-t-micro f-m-r-010'>{`${number_of_employees} Mitarbeiter`}</div>
              <Icon
                icon='person-16px'
                className='f-icon-020 f-c-st-pri f-no-shrink'
              />
            </div>
          )}
          {Boolean(year_of_foundation) && (
            <div className='f-d-flr f-fl-al-ce f-fl-jc-en f-m-b-005'>
              <div className='f-t-micro f-m-r-010'>{`seit ${year_of_foundation}`}</div>
              <Icon
                icon='flag-16px'
                className='f-icon-020 f-c-st-pri f-no-shrink'
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const VendorInfoPremium = ({
  name,
  slogan,
  year_of_foundation,
  number_of_employees,
  number_of_houses,
  branding_main_color,
  onClick,
  postcode,
  top_local_placement,
  children
}: VendorInfoPremiumProps) => {
  const hasVendorDetails =
    number_of_houses || number_of_employees || year_of_foundation
  return (
    <div
      className='f-d-flr f-fl-al-st f-fl-jc-sb f-m-t-010 f-m-b-005 SearchResultsVendor-vendor-info'
      onClick={onClick}>
      <div
        className={cx(
          'f-d-flc f-fl-al-st f-fl-jc-st f-fl-gap-005 SearchResultsVendor-vendor-name-slogan',
          hasVendorDetails
            ? 'SearchResultsVendor-vendor-name-slogan--is-column'
            : 'f-w-100'
        )}>
        <div className='f-t-tminor f-w-100 f-t-ellip'>{name}</div>
        {top_local_placement && postcode ? (
          <TopLocalVendorBadge />
        ) : (
          <div className='f-c-ba-pri600 f-m-v-005 f-d-flr f-fl-al-ce f-p-h-005 SearchResultsTile-premium-badge'>
            <Icon
              icon='star-filled-16px-fill'
              className='f-icon-020 f-c-st-whi f-c-fi-whi f-no-shrink'
            />
            <div className='f-t-micro f-c-fo-whi f-t-bold f-m-l-005'>
              Empfohlener Anbieter
            </div>
          </div>
        )}
        <div className='f-t-mini f-c-fo-neu200 f-w-100 f-t-ellip'>{slogan}</div>
        {children}
      </div>
      {hasVendorDetails && (
        <div className='f-d-flc f-fl-al-en f-fl-jc-ce f-fl-gap-005 f-no-shrink'>
          {Boolean(number_of_houses) && (
            <div className='f-d-flr f-fl-al-ce f-fl-jc-en f-m-b-005'>
              <div className='f-t-micro f-m-r-010'>
                {`${number_of_houses} ${
                  number_of_houses === 1 ? 'Haus' : 'Häuser'
                }`}
              </div>
              <Icon
                icon='house-16px'
                className='f-icon-020 f-no-shrink'
                color={branding_main_color}
              />
            </div>
          )}
          {Boolean(number_of_employees) && (
            <div className='f-d-flr f-fl-al-ce f-fl-jc-en f-m-b-005'>
              <div className='f-t-micro f-m-r-010'>{`${number_of_employees} Mitarbeiter`}</div>
              <Icon
                icon='person-16px'
                className='f-icon-020 f-no-shrink'
                color={branding_main_color}
              />
            </div>
          )}
          {Boolean(year_of_foundation) && (
            <div className='f-d-flr f-fl-al-ce f-fl-jc-en f-m-b-005'>
              <div className='f-t-micro f-m-r-010'>{`seit ${year_of_foundation}`}</div>
              <Icon
                icon='flag-16px'
                className='f-icon-020 f-no-shrink'
                color={branding_main_color}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const TopLocalVendorBadge = () => (
  <div className='f-c-ba-acc200 f-m-v-005 f-d-flr f-fl-al-ce f-p-h-005 SearchResultsTile-premium-badge'>
    <PinIcon />
    <div className='f-t-micro f-c-fo-whi f-t-bold f-m-l-005'>
      Top Lokaler Anbieter
    </div>
  </div>
)

interface ReviewsRatingProps {
  avg_rating: number
  count_total: number
  onClick: (event: React.MouseEvent<HTMLElement>) => void
}

export const ReviewsRating = ({
  avg_rating,
  count_total,
  onClick
}: ReviewsRatingProps) => {
  const rating = formatRating(avg_rating)
  return (
    <>
      <div
        className='f-m-b-005 SearchResultsVendor_review-container'
        onClick={onClick}>
        <span className='f-t-tmedium SearchResultsVendor_rating'>
          {rating === '0' ? '-' : rating}
        </span>
        <div className='SearchResultsVendor_rating-wrapper'>
          <SearchResultRating
            value={avg_rating}
            size={24}
            style={{
              strokeWidth: 0.5
            }}
            className='f-c-st-sec'
          />
        </div>
        <span className='f-c-fo-neu200 f-t-micro'>({count_total})</span>
      </div>
    </>
  )
}
